import React, { useState, useMemo } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  useTheme,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { withPageTracking } from "../../../services/postHog";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { Add, ChevronRight, Search } from "@mui/icons-material";
import { usePagination } from "../../../hooks/usePagination";
import { MyClassesOrAllClassesToggle } from "../../../shared-components/MyClassesOrAllClassesToggle";

const AssignmentGroupCard = ({ assignmentGroup }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          variant="h6"
          color="text.primary"
          gutterBottom
          sx={{ mb: 0 }}
        >
          {assignmentGroup.name}
        </Typography>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{ mt: 1.5, mb: 1 }}
        >
          <FruitifyIcon
            name={"Book"}
            sx={{
              width: 18,
              height: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Subject: </strong>
            {assignmentGroup.subject.name}
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{ mb: 1 }}
        >
          <FruitifyIcon
            name={"School"}
            sx={{
              width: 18,
              height: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Level: </strong>
            {assignmentGroup.educationLevel.level}
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{ mb: 1 }}
        >
          <FruitifyIcon
            name={"Assignment"}
            sx={{
              width: 18,
              height: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Assignments: </strong>
            {assignmentGroup.assignments.length}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", pt: 0, mt: -0.5 }}>
        <Button
          variant="card-action"
          color="primary"
          onClick={() => navigate(`/assignments/groups/${assignmentGroup.id}`)}
          endIcon={<ChevronRight />}
        >
          View Group
        </Button>
      </CardActions>
    </Card>
  );
};

const AssignmentGroupsOverview = () => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [showAllClasses, setShowAllClasses] = useState(false);

  const {
    options: assignmentGroups,
    page,
    pageInfo,
    handlePageChange,
    loading,
    error,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.assignment.getAllAssignmentGroups({
        first,
        after,
        showAllClasses,
      }),
    12,
    { showAllClasses },
  );

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return assignmentGroups
      .filter((g) =>
        selectedSubject ? g.subject.name === selectedSubject : true,
      )
      .filter((g) =>
        selectedLevel ? g.educationLevel.level === selectedLevel : true,
      )
      .filter((g) => {
        if (!term) return true;
        return (
          g.name.toLowerCase().includes(term) ||
          g.subject.name.toLowerCase().includes(term) ||
          g.educationLevel.level.toLowerCase().includes(term)
        );
      });
  }, [assignmentGroups, searchTerm, selectedSubject, selectedLevel]);

  const subjects = useMemo(() => {
    const subjectsSet = new Set(assignmentGroups.map((g) => g.subject.name));
    return Array.from(subjectsSet);
  }, [assignmentGroups]);

  const levels = useMemo(() => {
    const levelsSet = new Set(
      assignmentGroups.map((g) => g.educationLevel.level),
    );
    return Array.from(levelsSet);
  }, [assignmentGroups]);

  const handleCreateClick = () => {
    setGroupName("");
    setCreateDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    setGroupName("");
  };

  const handleContinue = () => {
    if (groupName.trim()) {
      setCreateDialogOpen(false);
      navigate(
        `/assignments/create?createGroupWithName=${encodeURIComponent(groupName.trim())}`,
      );
    }
  };

  if (loading) return <GeneralLoading />;
  if (error) {
    return (
      <FruitifyContainer>
        <Typography variant="h3" fontWeight="bold" mb={2}>
          Assignment Groups
        </Typography>
        <Typography color="error">Error loading assignment groups</Typography>
      </FruitifyContainer>
    );
  }

  return (
    <FruitifyContainer>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h3" fontWeight="bold">
          Assignment Groups
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleCreateClick}
        >
          Create
        </Button>
      </Box>

      <Dialog
        open={createDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Assignment Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Assignment Group Name"
            fullWidth
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && groupName.trim()) {
                handleContinue();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            disabled={!groupName.trim()}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {assignmentGroups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No assignment groups yet. Create an assignment group from any
              assignment to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%", mb: 3 }}
          >
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />

            <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
              <InputLabel id="subject-label">Subject</InputLabel>
              <Select
                labelId="subject-label"
                value={selectedSubject}
                label="Subject"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
              <InputLabel id="level-label">Education Level</InputLabel>
              <Select
                labelId="level-label"
                value={selectedLevel}
                label="Education Level"
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {levels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <MyClassesOrAllClassesToggle
              showAllClasses={showAllClasses}
              setShowAllClasses={setShowAllClasses}
            />
          </Stack>

          <Grid container spacing={2}>
            {filteredGroups.map((group) => (
              <Grid key={group.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <AssignmentGroupCard assignmentGroup={group} />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              page={page}
              onChange={handlePageChange}
              count={pageInfo.hasNextPage ? page + 1 : page}
              color="primary"
            />
          </Box>
        </>
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentGroupsOverview, {
  feature: "assignment-groups-overview",
});
